import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://westexlegal.com';
const SITE_NAME = 'WesTex Legal';
const DEFAULT_OG_IMAGE = `${SITE_URL}/lubbock-skyline.jpg`;

/**
 * Per-page SEO component.
 *
 * Sets <title>, meta description, canonical URL, Open Graph and Twitter card
 * meta tags. Drops in optional JSON-LD structured data (e.g., BreadcrumbList,
 * Service schemas) via the `schema` prop.
 *
 * @param {Object}  props
 * @param {string}  props.title          Page title (will append "| WesTex Legal")
 * @param {string}  props.description    Meta description (~150-160 chars ideal)
 * @param {string}  props.path           Page path WITHOUT domain (e.g., "/notary")
 * @param {string} [props.image]         OG image URL (absolute). Defaults to skyline.
 * @param {Object|Array} [props.schema]  Optional JSON-LD object or array of objects.
 * @param {boolean} [props.noindex]      Add <meta name="robots" content="noindex">
 */
export default function SEO({ title, description, path, image, schema, noindex }) {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const url = `${SITE_URL}${path || ''}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const schemaArray = Array.isArray(schema) ? schema : schema ? [schema] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={fullTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {schemaArray.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/** Convenience helper to build a BreadcrumbList schema for a page. */
export function buildBreadcrumb(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE };
